import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { del, get, post, put, type CaptionsVM, type CloudKey, type CloudPlan, type CloudProvider, type CloudRegion, type CostAccount, type VMPool, type VMPoolInput, type VMKind, type VMPoolsInfo, type VMWindow } from '../../api'
import { Button, Field } from '../../components/ui'
import { ago } from './format'
import type { Spec } from './states'
import { ErrorBox, NetPanel, SkeletonRows, StateText } from './ui'

/*
 * Network › Captions › Scheduled machines (owner 21.10, docs/captions.md
 * «Машины по расписанию»): captions nodes on rented GPU machines that the
 * center starts for weekly windows (America/New_York by default) and deletes
 * after them. Cloud keys (Vultr, Scaleway; AWS and Terraform are "soon"),
 * pools (where, which plan, how many, when, caps, the machine cost account),
 * their snapshot and their machines. The first machine of a pool installs
 * everything (~20 min); the next ones start from its snapshot.
 *
 * A pool is of a kind (TRANS-1 T6, docs/transcoder.md «Машины по
 * расписанию»): captions (a captions worker) or transcoder (a transcoder
 * node with our GPU engine; it may also start on demand, when a job finds
 * no transcoder node).
 */

const KIND: Record<VMKind, string> = { captions: 'Captions', transcoder: 'Transcoder' }

export const POOLS_KEY = ['admin-captions-pools']
export const CLOUD_KEYS_KEY = ['admin-cloud-keys']

const PROVIDER: Record<CloudProvider, string> = { vultr: 'Vultr', scaleway: 'Scaleway' }
const DEFAULT_REGION: Record<CloudProvider, string> = { vultr: 'ewr', scaleway: 'fr-par-2' }
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const TZS = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Europe/Paris', 'UTC']
const SEL = 'min-h-9 rounded-md border border-line-2 bg-bg px-2 text-[13px] text-text-soft focus-visible:border-accent max-md:min-h-[44px]'

const VM_SPEC: Record<CaptionsVM['state'], Spec> = {
  creating: { glyph: '◔', word: 'Creating', color: 'start', border: 'dashed', hint: 'Asked from the provider' },
  booting: { glyph: '◔', word: 'Installing', color: 'start', border: 'dashed', hint: 'Booting and installing the worker' },
  ready: { glyph: '●', word: 'Ready', color: 'live', border: 'solid', hint: 'Its worker is connected and takes jobs' },
  draining: { glyph: '◑', word: 'Draining', color: 'warn', border: 'dashed', hint: 'Out of its window: no new jobs, deleted when its jobs end' },
  deleting: { glyph: '◌', word: 'Deleting', color: 'idle', border: 'dotted', hint: 'The provider is deleting it' },
  deleted: { glyph: '✕', word: 'Deleted', color: 'idle', border: 'solid' },
  failed: { glyph: '✕', word: 'Failed', color: 'fail', border: 'solid', hint: 'Did not come up; deleted' },
}

/** [1,2,3,4,5] → "Mon–Fri"; [1,3] → "Mon, Wed". */
export function daysText(days: number[]): string {
  const d = [...days].sort((a, b) => a - b)
  const day = (n: number | undefined) => DAYS[(n ?? 1) - 1]
  if (d.length === 7) return 'Every day'
  const runs: string[] = []
  for (let i = 0; i < d.length; ) {
    let j = i
    while (j + 1 < d.length && d[j + 1] === (d[j] ?? 0) + 1) j++
    runs.push(j - i >= 2 ? `${day(d[i])}–${day(d[j])}` : d.slice(i, j + 1).map((x) => DAYS[x - 1]).join(', '))
    i = j + 1
  }
  return runs.join(', ')
}
const tzShort = (tz: string) => (tz === 'America/New_York' ? 'ET' : tz === 'America/Los_Angeles' ? 'PT' : tz === 'America/Chicago' ? 'CT' : tz === 'UTC' ? 'UTC' : tz)
export function scheduleText(p: Pick<VMPool, 'windows' | 'tz'>): string {
  if (!p.windows.length) return 'No windows: “Start now” only'
  return `${p.windows.map((w) => `${daysText(w.days)} ${w.from}–${w.to}`).join('; ')} ${tzShort(p.tz)}`
}
/** a time in the pool's zone: "Thu 17:30". */
function when(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz }).format(new Date(iso))
}
const money = (v: number, cur: string) => `${cur === 'EUR' ? '€' : '$'}${v.toFixed(v < 1 ? 3 : 2)}`

function PoolStatus({ p }: { p: VMPool }) {
  const alive = p.vms.filter((v) => !['deleted', 'failed'].includes(v.state))
  const ready = alive.filter((v) => v.state === 'ready').length
  let line
  switch (p.why) {
    case 'window':
      line = <StateText spec={{ glyph: '●', word: `In window · ${ready}/${p.want} ready`, color: 'live', border: 'solid' }} />
      break
    case 'manual':
      line = <StateText spec={{ glyph: '●', word: `Started by hand · ${ready}/${p.want} ready`, color: 'live', border: 'solid' }} />
      break
    case 'on_demand':
      line = <StateText spec={{ glyph: '●', word: `On demand · ${ready}/${p.want} ready`, color: 'live', border: 'solid', hint: 'A transcode job found no transcoder node' }} />
      break
    case 'paused':
      line = <StateText spec={{ glyph: '▲', word: 'Paused', color: 'warn', border: 'solid', hint: p.paused_reason }} />
      break
    case 'budget':
      line = <StateText spec={{ glyph: '■', word: 'Budget stop', color: 'fail', border: 'solid', hint: 'Its cost account is over the monthly budget' }} />
      break
    case 'no_center':
      line = <StateText spec={{ glyph: '▲', word: 'No public name', color: 'warn', border: 'solid' }} />
      break
    default:
      line = <StateText spec={{ glyph: '○', word: p.enabled ? (p.on_demand ? 'Waiting for demand' : 'Waiting') : 'Disabled', color: 'idle', border: 'dotted' }} />
  }
  return (
    <div className="flex flex-col gap-0.5 text-[13px]" data-testid={`pool-status-${p.id}`}>
      {line}
      <span className="text-xs text-muted">
        {p.why === 'paused' && p.paused_reason ? p.paused_reason
          : (p.why === 'window' || p.why === 'manual') && p.next_end ? `until ${when(p.next_end, p.tz)} ${tzShort(p.tz)}`
          : p.next_start ? `next start ${when(p.next_start, p.tz)} ${tzShort(p.tz)}` : alive.length ? `${alive.length} machine${alive.length > 1 ? 's' : ''} going` : ''}
      </span>
    </div>
  )
}

const SNAP: Record<VMPool['snapshot_state'], string> = {
  none: 'No snapshot yet: the first machine installs everything (~20 min), then it is snapshotted',
  creating: 'Snapshot being made from the first machine',
  ready: 'Snapshot ready: machines start from it in a few minutes',
  failed: 'The snapshot failed: machines install from scratch',
  unsupported: 'This provider makes no snapshot: every machine installs from scratch',
  skipped: 'No snapshot: this provider installs a machine faster than it starts one from a snapshot (Vultr: ~5 vs ~18 min)',
}

function MachinesTable({ p }: { p: VMPool }) {
  if (!p.vms.length) return <p className="text-sm text-muted">No machines in the last 24 h.</p>
  return (
    <div className="overflow-x-auto">
      <table className="sx-table compact [&_td]:whitespace-normal [&_th]:whitespace-normal w-full min-w-[640px]" data-testid={`pool-vms-${p.id}`}>
        <thead>
          <tr className="[&>th]:py-1 [&>th]:pr-3 [&>th]:font-medium">
            <th>Machine</th><th>State</th><th>Address</th><th>{p.kind === 'transcoder' ? 'Node' : 'Worker'}</th><th>Started</th><th>Note</th>
          </tr>
        </thead>
        <tbody>
          {p.vms.map((v) => (
            <tr key={v.id} data-testid={`vm-${v.id}`} className="[&>td]:py-1.5 [&>td]:pr-3 [&>td]:align-top">
              <td className="font-mono text-xs">{v.id}{v.from_snapshot ? <span className="text-muted"> · snapshot</span> : null}</td>
              <td>
                <StateText spec={VM_SPEC[v.state]} />
                {v.state === 'booting' && v.phase && <span className="block font-mono text-[11px] text-muted">phase: {v.phase}</span>}
              </td>
              <td className="tabular font-mono text-xs">{v.ip || '—'}</td>
              <td className="font-mono text-xs">{(v.kind === 'transcoder' ? v.node_id : v.worker_id) || '—'}</td>
              <td className="text-xs text-muted">{ago(v.created_at)}</td>
              <td className="max-w-64 truncate text-xs text-muted" title={v.last_error}>{v.last_error || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function WindowsEditor({ value, onChange }: { value: VMWindow[]; onChange: (w: VMWindow[]) => void }) {
  const set = (i: number, w: Partial<VMWindow>) => onChange(value.map((x, j) => (j === i ? { ...x, ...w } : x)))
  return (
    <div className="flex flex-col gap-2" data-testid="pool-windows">
      {value.map((w, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`pool-window-${i}`}>
          <span className="flex gap-0.5" role="group" aria-label={`Days of window ${i + 1}`}>
            {DAYS.map((d, k) => {
              const on = w.days.includes(k + 1)
              return (
                <button key={d} type="button" aria-pressed={on} onClick={() => set(i, { days: on ? w.days.filter((x) => x !== k + 1) : [...w.days, k + 1].sort() })}
                  className={`min-h-8 w-10 rounded-sm border text-xs max-md:min-h-[40px] ${on ? 'border-accent bg-accent/10 text-text' : 'border-line-2 text-muted'}`}>
                  {d}
                </button>
              )
            })}
          </span>
          <input aria-label={`From, window ${i + 1}`} type="time" value={w.from} onChange={(e) => set(i, { from: e.target.value })} className={SEL} />
          <span className="text-muted">–</span>
          <input aria-label={`To, window ${i + 1}`} type="time" value={w.to} onChange={(e) => set(i, { to: e.target.value })} className={SEL} />
          <Button variant="quiet" type="button" onClick={() => onChange(value.filter((_, j) => j !== i))}>Remove</Button>
        </div>
      ))}
      <span>
        <Button variant="plain" type="button" data-testid="pool-window-add" onClick={() => onChange([...value, { days: [1, 2, 3, 4, 5], from: '18:00', to: '23:00' }])}>Add window</Button>
      </span>
    </div>
  )
}

function PoolForm({ init, keys, accounts, onSave, onCancel, testId, fixedKind }: {
  init: VMPoolInput
  /** editing: the kind does not change */
  fixedKind?: boolean
  keys: CloudKey[]
  accounts: CostAccount[]
  onSave: (f: VMPoolInput) => Promise<void>
  onCancel: () => void
  testId: string
}) {
  const [f, setF] = useState(init)
  const [busy, setBusy] = useState(false)
  const key = keys.find((k) => k.id === f.cloud_key_id)
  const offers = useQuery({
    queryKey: ['cloud-offers', f.cloud_key_id, f.region],
    queryFn: () => get<{ regions: CloudRegion[]; plans?: CloudPlan[] }>(`/api/admin/captions/cloud-keys/${f.cloud_key_id}/offers?region=${encodeURIComponent(f.region)}`),
    enabled: !!f.cloud_key_id && !!f.region,
  })
  const plans = offers.data?.plans ?? []
  const plan = plans.find((p) => p.id === f.plan)
  const machines = accounts.filter((a) => a.kind === 'machine')
  const ok = f.name.trim() && f.cloud_key_id && f.region && f.plan
  const tc = f.kind === 'transcoder'
  return (
    <form className="flex flex-col gap-4" data-testid={testId}
      onSubmit={async (e) => {
        e.preventDefault()
        if (!ok) return
        setBusy(true)
        try {
          await onSave({ ...f, name: f.name.trim() })
        } finally {
          setBusy(false)
        }
      }}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Name" name="pool-name" value={f.name} maxLength={64} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Evening GPUs" />
        <label className="block">
          <span className="mb-1 block text-sm font-medium">Kind</span>
          <select name="pool-kind" className={`${SEL} w-full`} value={f.kind ?? 'captions'} disabled={fixedKind} title={fixedKind ? 'A pool keeps its kind: make a new pool' : undefined}
            onChange={(e) => {
              const kind = e.target.value as VMKind
              setF(kind === 'transcoder' ? { ...f, kind } : { ...f, kind, on_demand: false, node_region: '' })
            }}>
            {(['captions', 'transcoder'] as VMKind[]).map((k) => <option key={k} value={k}>{KIND[k]}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">Cloud key</span>
          <select name="pool-key" className={`${SEL} w-full`} value={f.cloud_key_id}
            onChange={(e) => {
              const k = keys.find((x) => x.id === e.target.value)
              setF({ ...f, cloud_key_id: e.target.value, region: k ? DEFAULT_REGION[k.provider] : '', plan: '' })
            }}>
            <option value="">Pick a key</option>
            {keys.map((k) => <option key={k.id} value={k.id}>{PROVIDER[k.provider]} · {k.label} · …{k.last4}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">Region</span>
          <select name="pool-region" className={`${SEL} w-full`} value={f.region} disabled={!key} onChange={(e) => setF({ ...f, region: e.target.value, plan: '' })}>
            {!offers.data?.regions.some((r) => r.id === f.region) && f.region && <option value={f.region}>{f.region}</option>}
            {(offers.data?.regions ?? []).map((r) => <option key={r.id} value={r.id}>{r.name} ({r.id})</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">GPU plan</span>
          <select name="pool-plan" className={`${SEL} w-full`} value={f.plan} disabled={!key || offers.isPending} onChange={(e) => setF({ ...f, plan: e.target.value })}>
            <option value="">{offers.isPending ? 'Loading…' : plans.length ? 'Pick a plan' : 'No GPU plans here'}</option>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.gpu} {p.vram_gb} GB · {p.vcpu} vCPU · {money(p.hourly, p.currency)}/h</option>)}
          </select>
        </label>
      </div>
      {offers.error && <ErrorBox text={(offers.error as Error).message} />}
      {key?.provider === 'scaleway' && <p className="text-xs text-muted">Scaleway has no US region: its GPUs are in Paris and Warsaw (~80 ms more to US nodes, fine for captions).</p>}
      <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Field label="Machines" name="pool-count" type="number" min={1} max={10} value={f.count} onChange={(e) => setF({ ...f, count: Number(e.target.value) })} />
        {!tc && <Field label="Streams each" name="pool-slots" type="number" min={1} max={64} value={f.slots} onChange={(e) => setF({ ...f, slots: Number(e.target.value) })} />}
        {tc && <Field label="Node region" name="pool-node-region" value={f.node_region ?? ''} maxLength={32} onChange={(e) => setF({ ...f, node_region: e.target.value.trim() })}
          placeholder="e.g. us-east" hint="The network region its nodes join; on-demand jobs of this region use it" />}
        <Field label="Start before, min" name="pool-lead" type="number" min={0} max={240} value={f.lead_minutes} onChange={(e) => setF({ ...f, lead_minutes: Number(e.target.value) })}
          hint="Install time from scratch ~20 min" />
        <Field label="Max life, h" name="pool-max" type="number" min={1} max={72} value={f.max_hours} onChange={(e) => setF({ ...f, max_hours: Number(e.target.value) })}
          hint="Deleted after it, jobs or not" />
        <label className="block">
          <span className="mb-1 block text-sm font-medium">Time zone</span>
          <select name="pool-tz" className={`${SEL} w-full`} value={f.tz} onChange={(e) => setF({ ...f, tz: e.target.value })}>
            {[...new Set([f.tz, ...TZS])].map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">Cost account</span>
          <select name="pool-account" className={`${SEL} w-full`} value={f.account_id ?? ''} onChange={(e) => setF({ ...f, account_id: e.target.value || null })}>
            <option value="">None</option>
            {machines.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
      </div>
      <div>
        <span className="mb-1 block text-sm font-medium">Windows</span>
        <WindowsEditor value={f.windows} onChange={(windows) => setF({ ...f, windows })} />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} /> Start machines for the windows
      </label>
      {tc && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" name="pool-on-demand" checked={!!f.on_demand} onChange={(e) => setF({ ...f, on_demand: e.target.checked })} />
          Also start on demand: when a transcode job finds no transcoder node (within the budget and the machine cap)
        </label>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" type="submit" disabled={!ok || busy} className="max-md:min-h-[44px]">Save pool</Button>
        <Button variant="quiet" type="button" onClick={onCancel}>Cancel</Button>
        {plan && (
          <span className="text-xs text-muted" data-testid="pool-estimate">
            {f.count} × {money(plan.hourly, plan.currency)}/h = {money(plan.hourly * f.count, plan.currency)}/h while running; at most {money(plan.hourly * f.count * f.max_hours, plan.currency)} per start
          </span>
        )}
      </div>
    </form>
  )
}

const blankPool = (k?: CloudKey): VMPoolInput => ({
  name: '', cloud_key_id: k?.id ?? '', region: k ? DEFAULT_REGION[k.provider] : '', plan: '', count: 1, slots: 8,
  windows: [{ days: [1, 2, 3, 4, 5], from: '18:00', to: '23:00' }], tz: 'America/New_York', lead_minutes: 30, max_hours: 12, enabled: true, account_id: null,
  kind: 'captions', on_demand: false, node_region: '',
})
const inputOf = (p: VMPool): VMPoolInput => ({
  name: p.name, cloud_key_id: p.cloud_key_id, region: p.region, plan: p.plan, count: p.count, slots: p.slots, windows: p.windows, tz: p.tz,
  lead_minutes: p.lead_minutes, max_hours: p.max_hours, enabled: p.enabled, account_id: p.account_id ?? null,
  kind: p.kind ?? 'captions', on_demand: !!p.on_demand, node_region: p.node_region ?? '',
})

function CloudKeys({ keys, onChanged, run }: { keys: CloudKey[]; onChanged: () => Promise<unknown>; run: (f: () => Promise<unknown>) => Promise<boolean> }) {
  const [adding, setAdding] = useState<CloudProvider | null>(null)
  const [label, setLabel] = useState('')
  const [secret, setSecret] = useState('')
  const [project, setProject] = useState('')
  const card = 'flex min-w-0 flex-col gap-2 rounded-md border border-line p-3'
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="cloud-keys">
      {(['vultr', 'scaleway'] as CloudProvider[]).map((prov) => (
        <div key={prov} className={card} data-testid={`cloud-${prov}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">{PROVIDER[prov]}</span>
            <Button variant="quiet" aria-expanded={adding === prov} data-testid={`cloud-add-${prov}`} onClick={() => setAdding((a) => (a === prov ? null : prov))}>{adding === prov ? 'Close' : 'Add key'}</Button>
          </div>
          {keys.filter((k) => k.provider === prov).map((k) => (
            <div key={k.id} className="flex items-center gap-2 text-[13px]" data-testid={`cloud-key-${k.id}`}>
              <span className="min-w-0 flex-1 truncate" title={k.label}>{k.label}</span>
              <span className="tabular font-mono text-xs text-muted">…{k.last4}</span>
              <span className="text-xs text-muted">{k.used_by.length ? `${k.used_by.length} pool${k.used_by.length > 1 ? 's' : ''}` : 'unused'}</span>
              <Button variant="quiet" disabled={k.used_by.length > 0} title={k.used_by.length ? 'Pools use it' : 'Delete the key'} data-testid={`cloud-key-delete-${k.id}`}
                onClick={() => void run(() => del(`/api/admin/captions/cloud-keys/${k.id}`))}>Delete</Button>
            </div>
          ))}
          {!keys.some((k) => k.provider === prov) && adding !== prov && <p className="text-xs text-muted">No key. It can create and delete machines: use a key of a separate account or project.</p>}
          {adding === prov && (
            <form className="flex flex-col gap-2" data-testid={`cloud-form-${prov}`}
              onSubmit={async (e) => {
                e.preventDefault()
                const okk = await run(() => post('/api/admin/captions/cloud-keys', { provider: prov, label: label.trim(), key: secret.trim(), ...(prov === 'scaleway' ? { project_id: project.trim() } : {}) }))
                if (okk) {
                  setAdding(null)
                  setLabel('')
                  setSecret('')
                  setProject('')
                  await onChanged()
                }
              }}>
              <Field label="Label" name={`cloud-label-${prov}`} value={label} maxLength={64} onChange={(e) => setLabel(e.target.value)} />
              <Field label={prov === 'scaleway' ? 'Secret key' : 'API key'} name={`cloud-key-${prov}`} type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Pasted once, never shown again" />
              {prov === 'scaleway' && <Field label="Project id" name="cloud-project" value={project} onChange={(e) => setProject(e.target.value)} placeholder="xxxxxxxx-xxxx-…" />}
              <Button variant="primary" type="submit" disabled={!label.trim() || secret.trim().length < 16 || (prov === 'scaleway' && !project.trim())}>Save key</Button>
            </form>
          )}
        </div>
      ))}
      {['AWS', 'Terraform'].map((x) => (
        <div key={x} className={`${card} opacity-70`} data-testid={`cloud-soon-${x.toLowerCase()}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">{x}</span>
            <span className="rounded-sm border border-line-2 px-1 font-mono text-[10px] leading-4 font-medium tracking-[.06em] text-muted">SOON</span>
          </div>
          <p className="text-xs text-muted">{x === 'AWS' ? 'EC2 GPU instances by the same schedule.' : 'Machines from your own Terraform module.'}</p>
        </div>
      ))}
    </div>
  )
}

export function MachinesPanel({ accounts }: { accounts: CostAccount[] }) {
  const qc = useQueryClient()
  const pools = useQuery({ queryKey: POOLS_KEY, queryFn: () => get<VMPoolsInfo>('/api/admin/captions/pools'), refetchInterval: 10_000 })
  const keys = useQuery({ queryKey: CLOUD_KEYS_KEY, queryFn: () => get<CloudKey[]>('/api/admin/captions/cloud-keys') })
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [hours, setHours] = useState<Record<string, number>>({})
  const [err, setErr] = useState('')
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: POOLS_KEY }), qc.invalidateQueries({ queryKey: CLOUD_KEYS_KEY })])
  const run = async (f: () => Promise<unknown>) => {
    setErr('')
    try {
      await f()
      await refresh()
      return true
    } catch (e) {
      setErr((e as Error).message)
      return false
    }
  }
  const list = pools.data?.pools ?? []
  const klist = keys.data ?? []
  const alive = list.flatMap((p) => p.vms).filter((v) => !['deleted', 'failed'].includes(v.state)).length
  return (
    <NetPanel
      pad={false}
      title="Scheduled machines"
      testId="captions-machines"
      aside={
        <span className="flex items-center gap-3">
          {pools.data && <span className="tabular font-mono text-xs text-muted" data-testid="machines-count">{alive} of {pools.data.max_machines} machines</span>}
          <Button variant="plain" data-testid="pool-add" aria-expanded={adding} disabled={!klist.length} title={klist.length ? undefined : 'Add a cloud key first'}
            className="max-md:min-h-[44px]" onClick={() => setAdding((a) => !a)}>{adding ? 'Close' : 'Add pool'}</Button>
        </span>
      }
    >
      <div className="flex flex-col gap-4 p-4">
        {err && <ErrorBox text={err} />}
        {pools.error && <ErrorBox text={(pools.error as Error).message} onRetry={() => void pools.refetch()} busy={pools.isFetching} />}
        {pools.data && !pools.data.center && (
          <p className="rounded-md border border-warn/50 bg-warn/[.07] px-3 py-2 text-sm" data-testid="machines-no-center">
            The center has no public name for the machines (APP_DOMAIN or CAPTIONS_VM_NAME): pools don’t start machines.
          </p>
        )}
        <CloudKeys keys={klist} onChanged={refresh} run={run} />
        {adding && (
          <div className="rounded-md border border-line p-3">
            <PoolForm init={blankPool(klist[0])} keys={klist} accounts={accounts} testId="pool-form-new" onCancel={() => setAdding(false)}
              onSave={async (f) => {
                if (await run(() => post('/api/admin/captions/pools', f))) setAdding(false)
              }} />
          </div>
        )}
        {pools.isPending ? (
          <SkeletonRows rows={2} cols={4} />
        ) : list.length === 0 ? (
          <p className="text-sm text-muted" data-testid="pools-empty">No pools. A pool starts GPU machines for its weekly windows and deletes them after; each machine is a captions node with its own worker, or a transcoder node.</p>
        ) : (
          <ul className="flex flex-col gap-3" data-testid="pools-list">
            {list.map((p) => {
              const aliveN = p.vms.filter((v) => !['deleted', 'failed'].includes(v.state)).length
              const k = klist.find((x) => x.id === p.cloud_key_id)
              return (
                <li key={p.id} data-testid={`pool-${p.id}`} className="flex flex-col gap-3 rounded-md border border-line p-3">
                  <div className="grid gap-3 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1.5fr)_minmax(0,1fr)_auto] md:items-start">
                    <div className="min-w-0">
                      <p className="flex items-center gap-2">
                        <span className="truncate font-medium">{p.name}</span>
                        <span className="flex-none rounded-sm border border-line-2 px-1 font-mono text-[10px] leading-4 font-medium tracking-[.06em] text-text-soft">{PROVIDER[p.provider].toUpperCase()}</span>
                        <span className="flex-none rounded-sm border border-line-2 px-1 font-mono text-[10px] leading-4 font-medium tracking-[.06em] text-text-soft" data-testid={`pool-kind-${p.id}`}>{KIND[p.kind ?? 'captions'].toUpperCase()}</span>
                      </p>
                      <p className="truncate font-mono text-xs text-muted">
                        {p.region} · {p.plan} · {p.kind === 'transcoder' ? `${p.count} machine${p.count > 1 ? 's' : ''}${p.node_region ? ` · node region ${p.node_region}` : ''}${p.on_demand ? ' · on demand' : ''}` : `${p.count} × ${p.slots} streams`}
                      </p>
                      <p className="truncate text-xs text-muted">{k ? `key ${k.label}` : ''}{p.account_id ? ` · cost ${accounts.find((a) => a.id === p.account_id)?.name ?? p.account_id}` : ' · no cost account'}</p>
                    </div>
                    <div className="min-w-0 text-[13px]">
                      <p data-testid={`pool-schedule-${p.id}`}>{scheduleText(p)}</p>
                      <p className="text-xs text-muted">starts {p.lead_minutes} min before · max {p.max_hours} h a machine</p>
                    </div>
                    <PoolStatus p={p} />
                    <div className="flex flex-wrap items-center gap-1 md:justify-end">
                      {p.why === 'manual' ? null : (
                        <span className="flex items-center gap-1">
                          <select aria-label={`Hours for ${p.name}`} className={SEL} value={hours[p.id] ?? 2} onChange={(e) => setHours({ ...hours, [p.id]: Number(e.target.value) })}>
                            {[1, 2, 3, 4, 6, 8, 12].map((h) => <option key={h} value={h}>{h} h</option>)}
                          </select>
                          <Button variant="plain" data-testid={`pool-start-${p.id}`} onClick={() => void run(() => post(`/api/admin/captions/pools/${p.id}/start`, { hours: hours[p.id] ?? 2 }))}>Start now</Button>
                        </span>
                      )}
                      {(p.enabled || p.why === 'manual' || aliveN > 0) && (
                        <Button variant="danger" data-testid={`pool-stop-${p.id}`} onClick={() => void run(() => post(`/api/admin/captions/pools/${p.id}/stop`))}>Stop</Button>
                      )}
                      <Button variant="quiet" data-testid={`pool-edit-${p.id}`} aria-expanded={editing === p.id} onClick={() => setEditing((e) => (e === p.id ? null : p.id))}>Edit</Button>
                      <Button variant="quiet" data-testid={`pool-delete-${p.id}`} disabled={aliveN > 0} title={aliveN ? 'Stop it and wait for its machines to go' : 'Delete the pool and its snapshot'}
                        onClick={() => void run(() => del(`/api/admin/captions/pools/${p.id}`))}>Delete</Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted" data-testid={`pool-snapshot-${p.id}`}>
                    <span>{SNAP[p.snapshot_state]}</span>
                    {(p.snapshot_state === 'ready' || p.snapshot_state === 'failed') && (
                      <Button variant="quiet" className="text-xs" onClick={() => void run(() => post(`/api/admin/captions/pools/${p.id}/rebuild`))}>Rebuild snapshot</Button>
                    )}
                  </div>
                  {editing === p.id ? (
                    <PoolForm init={inputOf(p)} keys={klist} accounts={accounts} testId={`pool-form-${p.id}`} fixedKind onCancel={() => setEditing(null)}
                      onSave={async (f) => {
                        if (await run(() => put(`/api/admin/captions/pools/${p.id}`, f))) setEditing(null)
                      }} />
                  ) : (
                    <MachinesTable p={p} />
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </NetPanel>
  )
}

